<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import Icon from 'vue-elements/icon/Icon.vue';
import PostalStep from '../components/wizard/PostalStep.vue';
import CurrentProviderStep from '../components/wizard/CurrentProviderStep.vue';
import ManualInputStep from '../components/wizard/ManualInputStep.vue';
import UsageTypeStep from '../components/wizard/UsageTypeStep.vue';
import HelpInputStep from '../components/wizard/HelpInputStep.vue';
import EstimationHelpStep from '../components/wizard/EstimationHelpStep.vue';
import HeatingTypeStep from '../components/wizard/HeatingTypeStep.vue';
import GoToComparerStep from '../components/wizard/GoToComparerStep.vue';
import Sidebar from '../components/Sidebar.vue';
import { openChat } from '../../../../assets/js/helpers/chat';
import { providers } from '../lib/filterOptions';
import { useEnergyComparisonStore } from '../stores/energyComparison';
import { filtersToUrlParams } from '../lib/filters';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas';
import CompareUspCard from '../components/CompareUspCard.vue';
import CombinedReviewCard from '../../reviews/combinedReviewCard/CombinedReviewCard.vue';

const props = defineProps({
  urlStep: String,
  urlPostcode: String,
  urlNumber: null,
  urlNumberExtension: null,
  urlFilters: null,
});

const comparisonStore = useEnergyComparisonStore();
const router = useRouter();

const scrollStep = ref(null);
const viewportHeight = ref(window.innerHeight || document.documentElement.clientHeight);
const noScroll = ref(false);

const filterQuery = computed(() => {
  const queryObject = {
    postcode: comparisonStore.postalCode,
    nr: comparisonStore.houseNumber,
    ...filtersToUrlParams(comparisonStore.filters),
  };

  if (comparisonStore.houseNumberAddition) {
    queryObject.extension = comparisonStore.houseNumberAddition;
  }

  return queryObject;
});

const mediumVersion = computed(() => router.currentRoute.value.query.medium === 'true');

const cssVars = computed(() => ({
  '--height': `${viewportHeight.value}px`,
}));

const hasAffiliateFlowSingleProvider = computed(() => {
  if (comparisonStore.filters.af && comparisonStore.filters.providers !== null && comparisonStore.filters.providers.length === 1) {
    return providers.some(provider => provider.value === comparisonStore.filters.providers[0]);
  }
  return false;
});

const affiliateFlowSingleProviderSlug = computed(() => {
  if (hasAffiliateFlowSingleProvider.value) {
    return comparisonStore.filters.providers[0];
  }
  return null;
});

const affiliateFlowSingleProviderName = computed(() => {
  if (hasAffiliateFlowSingleProvider.value) {
    const provider = providers.find(provider => provider.value === comparisonStore.filters.providers[0]);
    return provider ? provider.label : null;
  }
  return null;
});

const affiliateFlowHeader = computed(() => {
  if (comparisonStore.filters.aflabel !== null) {
    return comparisonStore.filters.aflabel;
  }
  if (affiliateFlowSingleProviderName.value != null) {
    return `${affiliateFlowSingleProviderName.value} aanbieding berekenen`;
  }
  return null;
});

const openChatProxy = (e) => {
  openChat(e.currentTarget);
};

const completeStep = ($event) => {
  if (comparisonStore.locationLoading) {
    scrollStep.value = $event;
  } else {
    scrollToNextStep($event);
  }
};

const scrollToStep = (id, delay = 240, position = 'start') => {
  const step = document.getElementById(id);
  if (step) {
    nextTick(() => {
      setTimeout(() => {
        step.scrollIntoView({ block: position });
      }, delay);
    });
  }
};
const scrollToNextStep = (currentStep, delay = 240) => {
  nextTick(() => {
    setTimeout(() => {
      if (currentStep && currentStep.nextElementSibling) {
        currentStep.nextElementSibling.scrollIntoView();
      }
    }, delay);
  });
};

const goToComparer = () => {
  if (!comparisonStore.compareLocation) {
    comparisonStore.error = false;
    document.getElementById('PostalStep').scrollIntoView({ block: 'center' });
    document.getElementById('zip-code').focus();
    nextTick(() => {
      comparisonStore.error = true;
      comparisonStore.errorMessage = 'Vul je postcode en huisnummer in om verder te gaan.';
    });
  } else {
    logAnalyticsEvent('compare-energy.wizard-submit',
      {
        current_provider: comparisonStore.filters.currentProvider,
        used_estimate: !comparisonStore.filters.manualInputStep,
        has_gas: !comparisonStore.filters.noGas,
        heating_type: comparisonStore.filters.heatingType,
        gas: comparisonStore.filters.gas,
        electricity: comparisonStore.filters.electricity,
        electricity_off_peak: comparisonStore.filters.electricityOffPeak,
        electricity_generation: comparisonStore.filters.electricityGeneration,
      }
    );

    comparisonStore.completeWizard();

    router.push({
      name: 'compare',
      query: filterQuery.value,
    });
  }
};

const scrollToRelevantStep = () => {
  if (props.urlFilters.step === 'PostalStep') {
    scrollToStep('PostalStep', 850, 'center');
  } else if (comparisonStore.filters.currentProvider === null) {
    scrollToStep('CurrentProviderStep', 850);
  } else if (comparisonStore.manualInputStep === null) {
    scrollToStep('EstimationHelpStep', 850);
  } else if (comparisonStore.wizardCompletionDate !== null) {
    const inputStep = comparisonStore.filters.manualInputStep ? 'ManualInputStep' : 'HelpInputStep';
    scrollToStep(inputStep, 850);
  }
};


onMounted(() => {
  const shouldLoadPostalLocation = props.urlPostcode !== undefined || props.urlNumber !== undefined;

  if (shouldLoadPostalLocation) {
    const hasDifferentPostalData = props.urlPostcode !== comparisonStore.postalCode
      || props.urlNumber !== comparisonStore.houseNumber
      || (props.urlNumberExtension && props.urlNumberExtension !== comparisonStore.houseNumberAddition);

    if (hasDifferentPostalData) {
      comparisonStore.loadPostalLocation(props.urlPostcode, props.urlNumber, props.urlNumberExtension)
        .then(() => {
          if (comparisonStore.compareLocation && comparisonStore.locationFound) {
            scrollToRelevantStep();
          }
        })
        .catch(() => {
          document.getElementById('PostalStep').scrollIntoView({ block: 'center' });
        });
    } else {
      scrollToRelevantStep();
    }
  }
  if (props.urlFilters) {
    Object.assign(comparisonStore.filters, props.urlFilters);
  }
});

watch(() => comparisonStore.locationLoading, (newValue) => {
  if (newValue === false && comparisonStore.compareLocation && comparisonStore.locationFound && scrollStep.value) {
    scrollToNextStep(scrollStep.value);
    scrollStep.value = null;
  }
}, { immediate: true });

watch(() => comparisonStore.compareLocation, (newValue) => {
  if (newValue) {
    comparisonStore.filters.noGas = newValue.has_gas === false;
    comparisonStore.showUsageTypeStep = false;
  }
});

const emit = defineEmits(['startPhase']);

emit('startPhase', 'wizard');
</script>

<template>
  <div class="energy-flow-container energy-flow-container wizard-container">
    <div class="energy-flow-view wizard">
      <div class="energy-flow-parts-container">
        <div v-if="hasAffiliateFlowSingleProvider" class="step-container">
          <div class="step-content">
            <div class="inline-tip">
              <strong>
                {{ affiliateFlowHeader }}
              </strong>
              <template v-if="comparisonStore.filters.afsource">
                <br><br>
                Je bent doorgestuurd vanaf {{ comparisonStore.filters.afsource }}.
              </template>
              <br>
              Op de volgende pagina zie je direct je persoonlijke aanbieding.
            </div>
          </div>
        </div>

        <PostalStep :style="cssVars" @complete="completeStep" />

        <template v-if="!mediumVersion">
          <CurrentProviderStep :style="cssVars" @complete="completeStep" />
        </template>

        <EstimationHelpStep :style="cssVars" @complete="completeStep" />

        <UsageTypeStep v-if="comparisonStore.showUsageTypeStep" :style="cssVars" @complete="completeStep" />

        <HeatingTypeStep
          v-if="!comparisonStore.compareLocation?.has_gas && comparisonStore.filters.manualInputStep === false"
          :style="cssVars" @complete="completeStep" />

        <HelpInputStep v-if="comparisonStore.filters.manualInputStep === false" :style="cssVars"
          @complete="completeStep" />

        <ManualInputStep v-else :style="cssVars" @complete="completeStep" />

        <GoToComparerStep :style="cssVars" @goToComparer="goToComparer()" />

      </div>
      <sidebar>
        <template #content>
          <img v-if="hasAffiliateFlowSingleProvider"
            :src="'https://energievergelijker.keuze.nl/logo/provider/' + affiliateFlowSingleProviderSlug"
            :alt="affiliateFlowSingleProviderName" class="aff-provider-logo">

          <div v-if="hasAffiliateFlowSingleProvider" class="tip">
            <h5>
              Vul je gegevens in voor {{ affiliateFlowSingleProviderName }}
            </h5>
            <span class="tip-text">
              Vul je gegevens in zodat wij kunnen uitrekenen wat je betaalt voor
              <strong>{{ affiliateFlowSingleProviderName }}.</strong>
            </span>
          </div>
          <template v-else>

            <div class="tip">
              <section class="desktop-tip">
                <h6>Vul je gegevens in</h6>
                <span class="tip-text">Met de gegevens die je hier invult rekenen wij uit wat de beste
                  energie deals voor je zijn.</span>
                <span class="tip-text">Heb je niet alle informatie bij de hand? Dan maken we een
                  inschatting.</span>
                <hr>
              </section>

              <h6>Hulp nodig?</h6>
              <span class="tip-text">
                Je vindt bij elke vraag altijd extra informatie als je dat nodig
                hebt. Of
                <span class="chat-hook" @click="openChatProxy">
                  chat
                </span>
                met een van onze experts.
              </span>

            </div>
          </template>

        </template>
        <template #usps>
          <div class="chat-times" @click="openChatProxy">
            <Icon icon="x-fa-comments" size="m" />
            <span> Chat open tot 22:00 </span>
          </div>
          <CompareUspCard class="sidebar-card"></CompareUspCard>
        </template>


      </sidebar>
    </div>
    <div class="flow-part-container">
      

      <div class="flow-part">
        <CompareUspCard class="mobile-card"></CompareUspCard>
      </div>
    </div>
  </div>
</template>
